// ReportsPage — single merged, paginated list of stored documents + completed
// inspection reports (previously two separate tables/sections).
import { useEffect, useMemo, useState } from "react";
import { Loader2, Download, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import { useAuth } from "@/context/AuthContext";
import { EmailReportDialog } from "@/components/EmailReportDialog";

const BASE_REPORT_CATEGORIES = ["Reports", "Site visit reports", "Accident investigation reports", "Certificates"];
const FIRE_REPORT_CATEGORIES = [
    "Existing fire documentation", "Fire strategies", "Fire risk assessments",
    "Fire door reports", "Fire compartmentation reports", "Structural fire protection reports",
];
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);
const PAGE_SIZE = 100;

function formatBytes(bytes) {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function toCsv(headers, rows) {
    return [headers, ...rows].map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
}

function downloadCsv(filename, headers, rows) {
    const blob = new Blob([toCsv(headers, rows)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
}

export default function ReportsPage() {
    const { user } = useAuth();
    const [allDocs, setAllDocs] = useState([]);
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfBusyId, setPdfBusyId] = useState(null);
    const [category, setCategory] = useState("__all__");     // "__all__" | "__instances__" | a document category
    const [emailTarget, setEmailTarget] = useState(null);     // { kind: "document"|"instance", id, title, clientId }
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [page, setPage] = useState(1);

    const reportCategories = useMemo(() => {
        const canSeeFire = user && (INTERNAL_ROLES.has(user.role) || user.role === "client_admin");
        return canSeeFire ? [...BASE_REPORT_CATEGORIES, ...FIRE_REPORT_CATEGORIES] : BASE_REPORT_CATEGORIES;
    }, [user]);

    useEffect(() => {
        Promise.all([
            api.get("/documents").catch(() => ({ data: [] })),
            api.get("/forms/instances", { params: { status: "Submitted" } }).catch(() => ({ data: [] })),
            api.get("/forms/instances", { params: { status: "Approved" } }).catch(() => ({ data: [] })),
        ]).then(([r1, i1, i2]) => {
            setAllDocs(r1.data);
            setInstances([...i1.data, ...i2.data]);
        }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load reports"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { setPage(1); }, [category, dateFrom, dateTo]);

    async function downloadDoc(d) {
        try {
            const resp = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: resp.headers["content-type"] || d.mime_type }));
            const a = document.createElement("a");
            a.href = url; a.download = d.filename || `report-${d.id.slice(0, 8)}`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
        }
    }

    async function downloadInstance(i) {
        if (pdfBusyId) return;
        setPdfBusyId(i.id);
        const t = toast.loading("Generating PDF… this can take a moment for reports with photos.");
        try {
            const resp = await api.get(`/forms/instances/${i.id}/pdf`, { responseType: "blob", timeout: 180000 });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(resp, `${i.reference || i.id.slice(0, 8)}.pdf`);
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("PDF ready", { id: t });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "PDF generation failed. Please try again in a moment.", { id: t });
        } finally {
            setPdfBusyId(null);
        }
    }

    // Merge documents + inspection instances into one flat row shape.
    const allRows = useMemo(() => {
        const docRows = allDocs.map((d) => ({
            key: `doc-${d.id}`, kind: "document", raw: d,
            title: d.title, type: d.category, client_name: d.client_name, project_name: d.project_name,
            status: d.status, created_by_name: d.created_by_name, date: d.created_at, size: d.size,
        }));
        const instRows = instances.map((i) => ({
            key: `inst-${i.id}`, kind: "instance", raw: i,
            title: i.title || i.template_name || "Inspection", type: i.template_name || "Inspection report",
            client_name: i.client_name, project_name: i.project_name, status: i.status,
            created_by_name: i.created_by_name, date: i.submitted_at || i.created_at, size: null,
        }));
        return [...docRows, ...instRows];
    }, [allDocs, instances]);

    const filteredRows = useMemo(() => {
        return allRows.filter((r) => {
            if (category === "__instances__" && r.kind !== "instance") return false;
            if (category !== "__all__" && category !== "__instances__" && (r.kind !== "document" || r.type !== category)) return false;
            const d = (r.date || "").slice(0, 10);
            if (!r.date) return !dateFrom && !dateTo;
            if (dateFrom && d < dateFrom) return false;
            if (dateTo && d > dateTo) return false;
            return true;
        });
    }, [allRows, category, dateFrom, dateTo]);

    if (loading) return <div className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>;
    return (
        <>
            <ReportsInner rows={filteredRows} downloadDoc={downloadDoc} downloadInstance={downloadInstance} pdfBusyId={pdfBusyId}
                category={category} setCategory={setCategory} reportCategories={reportCategories} onEmail={setEmailTarget}
                dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} page={page} setPage={setPage} />
            {emailTarget && <EmailReportDialog target={emailTarget} onClose={() => setEmailTarget(null)} />}
        </>
    );
}

function ReportsInner({ rows, downloadDoc, downloadInstance, pdfBusyId, category, setCategory, reportCategories, onEmail, dateFrom, setDateFrom, dateTo, setDateTo, page, setPage }) {
    const sort = useSortableRows(rows, {
        accessors: {
            title: (r) => r.title, type: (r) => r.type || "", client_name: (r) => r.client_name || "",
            project_name: (r) => r.project_name || "", status: (r) => r.status || "", created_by_name: (r) => r.created_by_name || "",
            date: (r) => r.date || "", size: (r) => r.size || 0,
        },
        defaultKey: "date", defaultDir: "desc",
    });

    const totalPages = Math.max(1, Math.ceil(sort.sorted.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageRows = sort.sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const rangeSuffix = (dateFrom || dateTo) ? `_${dateFrom || "start"}_to_${dateTo || "today"}` : "";
    const exportCsv = () => downloadCsv(`reports${rangeSuffix}.csv`,
        ["Title", "Type", "Client", "Project", "Status", "Created by", "Date", "Size"],
        sort.sorted.map((r) => [r.title, r.type || "", r.client_name || "", r.project_name || "", r.status || "", r.created_by_name || "", (r.date || "").slice(0, 10), r.kind === "document" ? formatBytes(r.size) : "—"]));

    return (
        <div data-testid="reports-page" className="space-y-6">
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                    style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Documents &amp; Forms</span>
                <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Reports</h2>
                <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                    {rows.length} report{rows.length === 1 ? "" : "s"} — download any with one click.
                </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap rounded-xl bg-white border border-[#E5E0DA] px-3 py-2.5">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Date range</span>
                <input type="date" data-testid="reports-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="h-8 px-2 rounded-lg border border-[#E5E0DA] bg-white text-[12px]" />
                <span className="text-[12px] text-[#9A968F]">to</span>
                <input type="date" data-testid="reports-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="h-8 px-2 rounded-lg border border-[#E5E0DA] bg-white text-[12px]" />
                {(dateFrom || dateTo) && (
                    <button data-testid="reports-date-clear" onClick={() => { setDateFrom(""); setDateTo(""); }}
                        className="text-[12px] text-[#2A5DB0] hover:underline">Clear</button>
                )}

                <span className="w-px h-5 bg-[#E5E0DA] mx-1" />

                <select data-testid="reports-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
                    className="h-8 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-[12px]">
                    <option value="__all__">All types (default)</option>
                    <option value="__instances__">Completed inspections (PDF)</option>
                    {reportCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                <button data-testid="reports-export-csv" onClick={exportCsv} disabled={!rows.length}
                    className="ml-auto inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#E5E0DA] bg-white text-[12px] text-[#2A5DB0] hover:bg-[#F5F3F0] disabled:opacity-50 disabled:cursor-not-allowed">
                    <Download size={12} /> Export CSV
                </button>
            </div>

            <section>
                <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                            <tr>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="title" onClick={sort.toggleSort}>Title</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="type" onClick={sort.toggleSort}>Type</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="client_name" onClick={sort.toggleSort}>Client</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="project_name" onClick={sort.toggleSort}>Project</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="status" onClick={sort.toggleSort}>Status</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="created_by_name" onClick={sort.toggleSort}>Created by</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="date" onClick={sort.toggleSort}>Date</SortableTh>
                                <SortableTh sortKey={sort.sortKey} sortDir={sort.sortDir} myKey="size" onClick={sort.toggleSort}>Size</SortableTh>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.length === 0 ? (
                                <tr><td colSpan={9} className="text-center py-10 text-[#9A968F]">No reports found for this filter.</td></tr>
                            ) : pageRows.map((r) => (
                                <tr key={r.key} data-testid={`report-row-${r.raw.id}`}
                                    className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0]">
                                    <td className="px-4 py-3 font-semibold text-[#1F1F1F]">{r.title}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.type || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.client_name || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.project_name || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.status || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.created_by_name || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px] whitespace-nowrap">{(r.date || "").slice(0, 10) || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64] text-[12px]">{r.kind === "document" ? formatBytes(r.size) : "—"}</td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        {r.kind === "document" ? (
                                            <button onClick={() => downloadDoc(r.raw)} data-testid={`report-doc-download-${r.raw.id}`}
                                                className="inline-flex items-center gap-1 text-[12px] text-[#2A5DB0] hover:underline">
                                                <Download size={12} /> Download
                                            </button>
                                        ) : (
                                            <button onClick={() => downloadInstance(r.raw)} data-testid={`report-instance-pdf-${r.raw.id}`}
                                                disabled={pdfBusyId === r.raw.id}
                                                className="inline-flex items-center gap-1 text-[12px] text-[#2A5DB0] hover:underline disabled:opacity-60 disabled:cursor-wait">
                                                {pdfBusyId === r.raw.id ? <><Loader2 size={12} className="animate-spin" /> Generating…</> : <><Download size={12} /> PDF</>}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => onEmail({ kind: r.kind, id: r.raw.id, title: r.title, clientId: r.raw.client_id })}
                                            data-testid={`report-email-${r.raw.id}`}
                                            className="inline-flex items-center gap-1 text-[12px] text-[#2A5DB0] hover:underline ml-3">
                                            <Mail size={12} /> Email to client
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {rows.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-2.5 border-t border-[#E5E0DA] bg-[#FAF9F7]">
                            <span className="text-[12px] text-[#6F6A64]">
                                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, rows.length)} of {rows.length}
                            </span>
                            <div className="flex items-center gap-2">
                                <button data-testid="reports-page-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}
                                    className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-[#E5E0DA] bg-white text-[12px] text-[#6F6A64] disabled:opacity-40 hover:bg-[#F5F3F0]">
                                    <ChevronLeft size={12} /> Prev
                                </button>
                                <span className="text-[12px] text-[#6F6A64]">Page {safePage} of {totalPages}</span>
                                <button data-testid="reports-page-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                                    className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-[#E5E0DA] bg-white text-[12px] text-[#6F6A64] disabled:opacity-40 hover:bg-[#F5F3F0]">
                                    Next <ChevronRight size={12} />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
